import { useEffect, useState, type ReactElement } from 'react';
import { Command, Menu, Moon, Search, Sun } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { ThemeMode } from '../hooks/useTheme';
import { Logo } from './Logo';

interface TopBarProps {
  theme: ThemeMode;
  onThemeChange: (theme: ThemeMode) => void;
  onOpenMenu: () => void;
}

export function TopBar({ theme, onThemeChange, onOpenMenu }: TopBarProps): ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const [value, setValue] = useState(() => new URLSearchParams(location.search).get('q') ?? '');

  useEffect(() => {
    setValue(new URLSearchParams(location.search).get('q') ?? '');
  }, [location.search]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const current = new URLSearchParams(location.search).get('q') ?? '';
      if (value.trim() === current) return;
      if (value.trim()) navigate(`/search?q=${encodeURIComponent(value.trim())}`, { replace: true });
      else if (location.pathname === '/search') navigate('/latest', { replace: true });
    }, 420);
    return () => window.clearTimeout(timer);
  }, [location.pathname, location.search, navigate, value]);

  const cycleTheme = (): void => {
    const next: ThemeMode = theme === 'system' ? 'dark' : theme === 'dark' ? 'light' : 'system';
    onThemeChange(next);
  };

  return (
    <header className="sticky top-0 z-30 border-b border-line/80 bg-canvas/95 backdrop-blur-xl">
      <div className="mx-auto flex h-[68px] max-w-[1540px] items-center gap-4 px-4 sm:px-6 lg:px-8">
        <button className="icon-button lg:hidden" aria-label="Apri menu" onClick={onOpenMenu}><Menu size={18} /></button>
        <Logo />
        <div className="hidden h-6 w-px bg-line sm:block" />
        <div className="hidden text-xs text-muted sm:block">Intelligence, senza rumore.</div>
        <div className="relative ml-auto w-full max-w-[420px]">
          <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" size={16} />
          <input
            aria-label="Cerca notizie"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            onKeyDown={(event) => { if (event.key === 'Enter') navigate(value.trim() ? `/search?q=${encodeURIComponent(value.trim())}` : '/latest'); }}
            placeholder="Cerca titoli, fonti, topic…"
            className="h-10 w-full rounded-xl border border-line bg-panel pl-9 pr-16 text-sm text-ink outline-none transition placeholder:text-subtle focus:border-accent focus:ring-4 focus:ring-indigo-400/10"
          />
          <span className="pointer-events-none absolute right-2.5 top-1/2 hidden -translate-y-1/2 items-center gap-1 rounded-md border border-line px-1.5 py-1 text-[10px] font-medium text-muted md:flex"><Command size={10} /> K</span>
        </div>
        <button className="icon-button" aria-label={`Tema: ${theme}. Cambia tema`} onClick={cycleTheme}>
          {theme === 'dark' ? <Moon size={17} /> : theme === 'light' ? <Sun size={17} /> : <span className="text-[11px] font-bold">A</span>}
        </button>
      </div>
    </header>
  );
}
